/**
 * Page 7: Usage & Cost (`/usage`) — Phase 8A, read-only.
 *
 * Two surfaces in one route:
 *
 * - **Tenant view** (any member): own totals, own quotas, and BYOK spend labelled `informational`
 *   because it is the tenant's provider bill, never ours.
 * - **Admin view** (platform ops): the provider matrix with consumed/limit/percent/reset, burn and
 *   projected exhaustion, cost-per-build vs cost-per-run, top tenants, reconciliation drift and the
 *   four alert-only tripwires.
 *
 * Nothing on this page can refuse an action, and it says so: a bar at 100% is an observation, not a
 * cap. That sentence is deliberate — a usage page that looks like a quota page invites the belief
 * that something is being blocked when nothing is.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import {
  adminUsageCsv,
  barPercent,
  barTone,
  formatHours,
  formatMicros,
  USAGE_WINDOWS,
  type AdminUsage,
  type BreakdownRow,
  type HeadroomRow,
  type TenantUsage,
  type UsageWindow,
} from "../lib/usage";
import { ApiError, authToken, apiBaseUrl } from "../api";

type View = "tenant" | "admin";

async function fetchJson<T>(path: string): Promise<T> {
  const response = await fetch(`${apiBaseUrl()}${path}`, {
    headers: { authorization: `Bearer ${authToken()}` },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new ApiError(text || `request failed (${response.status})`, response.status);
  }
  return (await response.json()) as T;
}

function Bar({ percent }: { percent: number | null }) {
  const fill = barPercent(percent);
  const tone = barTone(percent);
  if (fill === null) {
    return (
      <div className="tj-usage-bar" data-tone="unknown" aria-label="not measured">
        <span className="tj-usage-bar-empty">not measured</span>
      </div>
    );
  }
  return (
    <div className="tj-usage-bar" data-tone={tone}>
      <span className="tj-usage-bar-fill" style={{ width: `${fill}%` }} />
    </div>
  );
}

function MoneyCell({ cost, shadow }: { cost: number; shadow: number }) {
  return (
    <div className="tj-usage-money">
      <span className="tj-usage-money-now">{formatMicros(cost)}</span>
      <span className="tj-usage-money-shadow" title="what this would cost at paid rates">
        {formatMicros(shadow)} at paid rates
      </span>
    </div>
  );
}

function BreakdownTable({ title, rows }: { title: string; rows: BreakdownRow[] }) {
  if (rows.length === 0) {
    return (
      <section className="tj-card">
        <h2 className="serif-heading text-lg">{title}</h2>
        <p className="text-sm text-[var(--tj-muted)]">No activity in this window.</p>
      </section>
    );
  }
  return (
    <section className="tj-card" data-testid="usage-breakdown">
      <h2 className="serif-heading text-lg">{title}</h2>
      <table className="tj-table">
        <thead>
          <tr>
            <th scope="col">Key</th>
            <th scope="col">Events</th>
            <th scope="col">Quantity</th>
            <th scope="col">Cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>{row.key}</td>
              <td>{row.events.toLocaleString("en-US")}</td>
              <td>{row.quantity.toLocaleString("en-US")}</td>
              <td>
                <MoneyCell cost={row.cost_micros} shadow={row.shadow_cost_micros} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function QuotaList({ rows }: { rows: HeadroomRow[] }) {
  return (
    <section className="tj-card" data-testid="usage-quota">
      <h2 className="serif-heading text-lg">Quotas</h2>
      <p className="text-xs text-[var(--tj-muted)]">
        Measurement only: nothing here is enforced, and a full bar refuses no request.
      </p>
      <ul className="tj-usage-quotas">
        {rows.map((row) => (
          <li key={row.key}>
            <div className="tj-usage-quota-head">
              <span className="tj-usage-quota-name">{row.key}</span>
              <span className="tj-usage-quota-count">
                {row.measured
                  ? `${row.consumed.toLocaleString("en-US")} / ${row.limit.toLocaleString("en-US")}`
                  : "not measured"}
              </span>
            </div>
            <Bar percent={row.percent} />
            <div className="tj-usage-quota-foot">
              <span>{row.period}</span>
              <span>
                {row.measured
                  ? `${row.remaining.toLocaleString("en-US")} left`
                  : "no collector feeds this yet"}
              </span>
              {row.reset_at ? <span>resets {new Date(row.reset_at).toLocaleString()}</span> : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TenantView({ data }: { data: TenantUsage }) {
  return (
    <div className="space-y-4" data-testid="usage-tenant">
      <section className="tj-card">
        <h2 className="serif-heading text-lg">This workspace</h2>
        <div className="tj-usage-totals">
          <div>
            <span className="tj-usage-label">Spend now</span>
            <strong>{formatMicros(data.totals.cost_micros)}</strong>
          </div>
          <div>
            <span className="tj-usage-label">At paid rates</span>
            <strong>{formatMicros(data.totals.shadow_cost_micros)}</strong>
          </div>
          <div>
            <span className="tj-usage-label">Metered events</span>
            <strong>{data.events.toLocaleString("en-US")}</strong>
          </div>
        </div>
        {data.byok.events > 0 ? (
          <p className="tj-usage-byok" data-testid="usage-byok">
            Bring-your-own-key: {data.byok.events.toLocaleString("en-US")} model calls,{" "}
            {data.byok.tokens.toLocaleString("en-US")} tokens,{" "}
            {formatMicros(data.byok.cost_micros)} of <em>your</em> provider spend. Informational: it
            is not billed by TaskJinn and is never mixed into the totals above.
          </p>
        ) : null}
      </section>

      <BreakdownTable title="By provider" rows={data.by_provider} />
      <BreakdownTable title="By resource" rows={data.by_resource} />
      <BreakdownTable title="By purpose" rows={data.by_purpose} />
      <QuotaList rows={data.quota} />
    </div>
  );
}

function AdminView({ data }: { data: AdminUsage }) {
  const download = useCallback(() => {
    const blob = new Blob([adminUsageCsv(data)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `taskjinn-usage-${data.window}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [data]);

  const { build_vs_run: buildRun } = data;

  return (
    <div className="space-y-4" data-testid="usage-admin">
      <section className="tj-card">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="serif-heading text-lg">Platform</h2>
            <p className="text-xs text-[var(--tj-muted)]">{data.note}</p>
          </div>
          <button type="button" className="tj-btn tj-btn-secondary" onClick={download}>
            Export CSV
          </button>
        </div>
        <div className="tj-usage-totals">
          <div>
            <span className="tj-usage-label">Spend now</span>
            <strong>{formatMicros(data.totals.cost_micros)}</strong>
          </div>
          <div>
            <span className="tj-usage-label">Month-to-date at paid rates</span>
            <strong>{formatMicros(data.totals.shadow_cost_micros)}</strong>
          </div>
          <div>
            <span className="tj-usage-label">Metered events</span>
            <strong>{data.events.toLocaleString("en-US")}</strong>
          </div>
        </div>
      </section>

      <section className="tj-card" data-testid="usage-build-vs-run">
        <h2 className="serif-heading text-lg">Build vs run</h2>
        <p className="text-xs text-[var(--tj-muted)]">
          Authoring a template costs orders of magnitude more than executing it — the asymmetry the
          whole pricing model rests on.
        </p>
        <div className="tj-usage-totals">
          <div>
            <span className="tj-usage-label">Build</span>
            <strong>{formatMicros(buildRun.build_cost_micros)}</strong>
            <span className="tj-usage-sub">{buildRun.build_tokens.toLocaleString("en-US")} tokens</span>
          </div>
          <div>
            <span className="tj-usage-label">Runs (review + fallback)</span>
            <strong>{formatMicros(buildRun.run_cost_micros)}</strong>
            <span className="tj-usage-sub">{buildRun.run_tokens.toLocaleString("en-US")} tokens</span>
          </div>
          <div>
            <span className="tj-usage-label">Ratio</span>
            <strong>
              {buildRun.ratio === null ? "—" : `${Math.round(buildRun.ratio).toLocaleString("en-US")}×`}
            </strong>
            {buildRun.ratio === null ? (
              <span className="tj-usage-sub">needs both builds and runs in the window</span>
            ) : null}
          </div>
        </div>
      </section>

      {data.providers.map((provider) => (
        <section className="tj-card" key={provider.provider} data-testid="usage-provider">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="serif-heading text-lg">{provider.provider}</h2>
            <MoneyCell cost={provider.cost_micros} shadow={provider.shadow_cost_micros} />
          </div>
          <ul className="tj-usage-quotas">
            {provider.resources.map((resource) => (
              <li key={resource.resource}>
                <div className="tj-usage-quota-head">
                  <span className="tj-usage-quota-name">{resource.resource}</span>
                  <span className="tj-usage-quota-count">
                    {resource.quantity.toLocaleString("en-US")}
                    {resource.limit === null
                      ? ""
                      : ` / ${resource.limit.toLocaleString("en-US")}`}
                  </span>
                </div>
                <Bar percent={resource.percent} />
                <div className="tj-usage-quota-foot">
                  <span>
                    {resource.percent === null
                      ? "no recorded ceiling"
                      : `${resource.percent.toFixed(1)}% of ${resource.period ?? "window"}`}
                  </span>
                  <span>
                    {resource.hours_to_limit === null
                      ? "—"
                      : `ceiling in ${formatHours(resource.hours_to_limit)} at this rate`}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}

      <section className="tj-card" data-testid="usage-tenants">
        <h2 className="serif-heading text-lg">Top accounts by cost</h2>
        <table className="tj-table">
          <thead>
            <tr>
              <th scope="col">Account</th>
              <th scope="col">Events</th>
              <th scope="col">Cost</th>
            </tr>
          </thead>
          <tbody>
            {data.tenants.map((tenant) => (
              <tr key={tenant.account_id}>
                <td>
                  {tenant.label || tenant.account_id}
                  {tenant.label ? (
                    <span className="tj-usage-sub"> {tenant.account_id}</span>
                  ) : null}
                </td>
                <td>{tenant.events.toLocaleString("en-US")}</td>
                <td>
                  <MoneyCell cost={tenant.cost_micros} shadow={tenant.shadow_cost_micros} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="tj-card" data-testid="usage-reconciliation">
        <h2 className="serif-heading text-lg">
          Reconciliation {data.reconciliation.day ? `· ${data.reconciliation.day}` : ""}
        </h2>
        <p className="text-xs text-[var(--tj-muted)]">
          Local ledger against the provider&apos;s own figure.{" "}
          {data.reconciliation.last_polled_at
            ? `Last poll ${new Date(data.reconciliation.last_polled_at).toLocaleString()}.`
            : "No poll has run yet, so every row is unknown — not agreement."}
        </p>
        {data.reconciliation.rows.length === 0 ? (
          <p className="text-sm text-[var(--tj-muted)]">Nothing reconciled for that day.</p>
        ) : (
          <table className="tj-table">
            <thead>
              <tr>
                <th scope="col">Provider</th>
                <th scope="col">Resource</th>
                <th scope="col">Local</th>
                <th scope="col">Provider</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.reconciliation.rows.map((row) => (
                <tr key={`${row.provider}:${row.resource}`} data-status={row.status}>
                  <td>{row.provider}</td>
                  <td>{row.resource}</td>
                  <td>{row.local_quantity.toLocaleString("en-US")}</td>
                  <td>
                    {row.provider_quantity === null
                      ? "—"
                      : row.provider_quantity.toLocaleString("en-US")}
                  </td>
                  <td>
                    {row.status}
                    {row.source_kind === "analytics" ? (
                      <span className="tj-usage-sub"> (analytics: upper bound)</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="tj-card" data-testid="usage-tripwires">
        <h2 className="serif-heading text-lg">Tripwires</h2>
        <p className="text-xs text-[var(--tj-muted)]">
          Alert-only. They notify; they never refuse a request — enforcement is a Part 8B decision
          with a written record behind it.
        </p>
        <ul className="tj-usage-tripwires">
          {data.tripwires.map((row) => (
            <li key={row.key} data-tripped={row.tripped}>
              <span className="tj-usage-quota-name">{row.key}</span>
              <span className="tj-usage-quota-count">
                {row.observed.toLocaleString("en-US")} / {row.threshold.toLocaleString("en-US")}
              </span>
              <span className="tj-badge">{row.tripped ? "tripped" : "clear"}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export function UsagePage() {
  const { isAdmin, session } = useAuth();
  const [window, setWindow] = useState<UsageWindow>("7d");
  const [view, setView] = useState<View>("tenant");
  const [tenant, setTenant] = useState<TenantUsage | null>(null);
  const [admin, setAdmin] = useState<AdminUsage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (view === "admin") {
        setAdmin(await fetchJson<AdminUsage>(`/api/v1/admin/usage?window=${window}`));
      } else {
        setTenant(await fetchJson<TenantUsage>(`/api/v1/usage?window=${window}`));
      }
    } catch (cause) {
      // A 403 on the admin view is not an error to shout about: the caller is simply not platform
      // ops, so the tab is not offered in the first place. Anything else is shown.
      setError(cause instanceof Error ? cause.message : "Could not load usage");
    } finally {
      setLoading(false);
    }
  }, [view, window]);

  useEffect(() => {
    if (session) void load();
  }, [session, load]);

  const windows = useMemo(
    () =>
      USAGE_WINDOWS.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`tj-btn ${window === option.value ? "tj-btn-primary" : "tj-btn-secondary"}`}
          aria-pressed={window === option.value}
          onClick={() => setWindow(option.value)}
        >
          {option.label}
        </button>
      )),
    [window]
  );

  if (!session) return null;

  return (
    <main className="tj-container max-w-5xl space-y-4 py-6" data-testid="usage-page">
      <header className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-[var(--tj-muted)]">Usage &amp; cost</p>
        <h1 className="serif-heading text-2xl">What this costs</h1>
        <p className="text-sm text-[var(--tj-muted)]">
          Measured from the usage ledger. Two numbers per line: what it costs today (nothing — every
          provider is on a free plan) and what the same traffic would cost at paid rates.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {windows}
          {isAdmin ? (
            <div className="tj-usage-viewswitch" role="group" aria-label="View">
              <button
                type="button"
                className={`tj-btn ${view === "tenant" ? "tj-btn-primary" : "tj-btn-secondary"}`}
                aria-pressed={view === "tenant"}
                onClick={() => setView("tenant")}
              >
                This workspace
              </button>
              <button
                type="button"
                className={`tj-btn ${view === "admin" ? "tj-btn-primary" : "tj-btn-secondary"}`}
                aria-pressed={view === "admin"}
                onClick={() => setView("admin")}
              >
                Platform
              </button>
            </div>
          ) : null}
        </div>
      </header>

      {error ? (
        <p className="tj-error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? <p className="text-sm text-[var(--tj-muted)]">Loading…</p> : null}

      {!loading && !error && view === "tenant" && tenant ? <TenantView data={tenant} /> : null}
      {!loading && !error && view === "admin" && admin ? <AdminView data={admin} /> : null}

      <footer className="text-xs text-[var(--tj-muted)]">
        Part 8A: measurement only. No limit on this page is enforced, and no request was refused to
        produce it.
      </footer>
    </main>
  );
}
